import { DurationType, ScheduleType } from '../modules/privateOffer/reducer'
import { getCappedScheduledInstallmentCount } from '../../common/utils/constants'
import { addMonths, addQuarters, addYears } from 'date-fns'
import { DateTime } from 'luxon'

export const getPricingModel = (
  flexiblePaymentTerms: boolean | undefined,
  containerOfferServiceLength: string | null | undefined
): string => {
  if (flexiblePaymentTerms) {
    return 'installment-plan'
  } else if (!flexiblePaymentTerms && containerOfferServiceLength) {
    return 'upfront-payment'
  }
  return 'usage-pricing'
}

const calculateInstallmentDate = (
  startDate: string,
  flexiblePaymentType: ScheduleType,
  installmentNumber: number
): string => {
  let calculatedDate: Date
  switch (flexiblePaymentType) {
    case ScheduleType.MONTHLY:
      calculatedDate = addMonths(new Date(startDate), installmentNumber)
      break
    case ScheduleType.QUARTERLY:
      calculatedDate = addQuarters(new Date(startDate), installmentNumber)
      break
    case ScheduleType.YEARLY:
      calculatedDate = addYears(new Date(startDate), installmentNumber)
      break
    default:
      calculatedDate = new Date(startDate)
  }
  const formattedDate = DateTime.fromJSDate(calculatedDate).toISODate()
  return formattedDate || calculatedDate.toISOString().split('T')[0] // fallback
}

const calculateNumberOfInstallments = (
  flexiblePaymentType: ScheduleType,
  serviceLength?: number
): number => {
  if (serviceLength) {
    switch (flexiblePaymentType) {
      case ScheduleType.MONTHLY:
        return Math.ceil(serviceLength / 1)
      case ScheduleType.QUARTERLY:
        return Math.ceil(serviceLength / 3)
      case ScheduleType.YEARLY:
        return Math.ceil(serviceLength / 12)

      default:
        return 1
    }
  }
  return 1
}

export const getAmiInstallmentsBasedOnSchedule = ({
  durationInMonths,
  now,
  paymentSchedule,
  tcv,
}: {
  tcv: number
  durationInMonths: number
  paymentSchedule: ScheduleType
  now: string
}) => {
  const quantityOfInstallments = getCappedScheduledInstallmentCount(
    calculateNumberOfInstallments(
      paymentSchedule || ScheduleType.CUSTOM,
      durationInMonths
    )
  )

  const base = Math.floor(tcv / quantityOfInstallments)
  const remainder = tcv - base * quantityOfInstallments

  return Array.from({ length: quantityOfInstallments }).map((_, index) => {
    return {
      amount: base + (index === 0 ? remainder : 0),
      paymentDate: calculateInstallmentDate(
        now || new Date().toISOString(),
        paymentSchedule || ScheduleType.CUSTOM,
        index
      ),
    }
  })
}

export const calculateUsageEndDateFromDuration = (
  offerExpirationDate: string | null | undefined,
  usageDuration: number | string | null | undefined,
  format: string = 'yyyy-MM-dd'
): string | null => {
  if (!offerExpirationDate || !usageDuration) {
    return null
  }

  try {
    const expirationDate = DateTime.fromISO(offerExpirationDate)
    if (!expirationDate.isValid) {
      return null
    }

    const durationDays =
      typeof usageDuration === 'string'
        ? parseInt(usageDuration, 10)
        : usageDuration

    if (isNaN(durationDays) || durationDays <= 0) {
      return null
    }

    const endDate = expirationDate.plus({ days: durationDays })
    return endDate.toFormat(format).toLocaleString()
  } catch (error) {
    return null
  }
}

export const parseDurationFromServiceLength = (
  containerOfferServiceLength: string | null | undefined
): { durationType: DurationType | null; durationLength: number | null } => {
  if (!containerOfferServiceLength) {
    return { durationType: null, durationLength: null }
  }

  try {
    const serviceLength = containerOfferServiceLength.toString()
    // Parse string format like "12M", "1Y", "30D", "4W"
    const match = serviceLength.match(/^(\d+(?:\.\d+)?)([MYWDH])?$/i)

    if (!match) {
      return { durationType: null, durationLength: null }
    }

    const value = parseFloat(match[1])
    const unit = match[2]?.toUpperCase()

    if (isNaN(value) || value < 0) {
      return { durationType: null, durationLength: null }
    }

    let durationType: DurationType | null = null

    switch (unit) {
      case 'Y':
        durationType = DurationType.YEARS
        break
      case 'M':
        durationType = DurationType.MONTHS
        break
      case 'W':
        durationType = DurationType.WEEKS
        break
      case 'D':
        durationType = DurationType.DAYS
        break
      case 'H':
        // Hours are not supported in DurationType, return null
        return { durationType: null, durationLength: null }
      default:
        // If no unit, assume months
        durationType = DurationType.MONTHS
    }

    return {
      durationType,
      durationLength: value > 0 ? Math.round(value * 100) / 100 : null, // Round to 2 decimal places
    }
  } catch (error) {
    return { durationType: null, durationLength: null }
  }
}

export const calculateUsageDurationFromDates = ({
  offerExpirationDate = new Date().toISOString(),
  usageEndDate,
}: {
  offerExpirationDate?: string | null | undefined
  usageEndDate: string | null | undefined
}): number | null => {
  if (!usageEndDate) {
    return null
  }

  try {
    const expirationDate = DateTime.fromISO(offerExpirationDate!)
    const endDate = DateTime.fromISO(usageEndDate)

    if (!expirationDate.isValid || !endDate.isValid) {
      return null
    }

    const diffInDays = Math.ceil(endDate.diff(expirationDate, 'days').days)

    return diffInDays > 0 ? diffInDays : null
  } catch (error) {
    console.error('Error calculating usage duration', error)
    return null
  }
}

export const getUsageDuration = (
  usageDuration: number | string | null | undefined | unknown,
  offerExpirationDate: string | null | undefined,
  usageEndDate: string | null | undefined
): number | null => {
  if (
    usageDuration !== null &&
    usageDuration !== undefined &&
    usageDuration !== ''
  ) {
    // Convert to number if it's a string
    const numericValue =
      typeof usageDuration === 'string'
        ? parseFloat(usageDuration)
        : typeof usageDuration === 'number'
          ? usageDuration
          : Number(usageDuration)
    // Return the numeric value if it's valid
    if (!isNaN(numericValue) && numericValue >= 0) {
      return numericValue
    }
  }

  // If usageDuration is invalid, calculate from dates
  return calculateUsageDurationFromDates({ offerExpirationDate, usageEndDate })
}
